import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AirplaneTilt, User, Package } from '@phosphor-icons/react';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const UserTypeSelection = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedType) {
      toast.error('Please select a user type');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_type: selectedType })
      });

      if (!response.ok) throw new Error('Failed to update profile');

      toast.success('Profile updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to update profile');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9FAFB] to-white flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <AirplaneTilt size={64} weight="duotone" className="text-[#6D28D9]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="user-type-heading">
            Welcome to Terminal
          </h1>
          <p className="text-lg text-gray-600 font-medium">Choose how you'd like to use Terminal</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <button
            onClick={() => setSelectedType('traveler')}
            className={`p-8 rounded-xl border-2 transition-all duration-200 text-left ${
              selectedType === 'traveler'
                ? 'border-[#6D28D9] bg-[#6D28D9]/10 shadow-lg -translate-y-1'
                : 'border-gray-200 bg-white hover:border-[#6D28D9]/50 hover:shadow-lg hover:-translate-y-1'
            }`}
            data-testid="select-traveler"
          >
            <AirplaneTilt size={48} weight="duotone" className={selectedType === 'traveler' ? 'text-[#6D28D9]' : 'text-gray-500'} />
            <h3 className="text-2xl font-bold text-gray-900 mt-4 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              I'm a Traveler
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Earn money by utilizing your spare luggage capacity. Accept delivery requests and help parcels reach their destination.
            </p>
          </button>

          <button
            onClick={() => setSelectedType('sender')}
            className={`p-8 rounded-xl border-2 transition-all duration-200 text-left ${
              selectedType === 'sender'
                ? 'border-[#6D28D9] bg-[#6D28D9]/10 shadow-lg -translate-y-1'
                : 'border-gray-200 bg-white hover:border-[#6D28D9]/50 hover:shadow-lg hover:-translate-y-1'
            }`}
            data-testid="select-sender"
          >
            <Package size={48} weight="duotone" className={selectedType === 'sender' ? 'text-[#6D28D9]' : 'text-gray-500'} />
            <h3 className="text-2xl font-bold text-gray-900 mt-4 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              I'm a Sender
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Send parcels internationally through verified travelers. Find the perfect match for your delivery needs.
            </p>
          </button>
        </div>

        <div className="text-center">
          <Button
            onClick={handleSubmit}
            disabled={!selectedType || loading}
            className="px-12 py-6 text-lg bg-#1A1A2E hover:bg-[#133A60] text-gray-900 rounded-full font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="confirm-user-type"
          >
            {loading ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserTypeSelection;
